import {
  ArrowRightIcon,
  BeakerIcon,
  BuildingOffice2Icon,
  CalendarDaysIcon,
  ChartBarSquareIcon,
  ClipboardDocumentCheckIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  HeartIcon,
  ShoppingCartIcon,
  UserGroupIcon,
  UserCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loadDoctorDashboard } from "../../store/slices/dashboardSlice";

const KPI_CARDS = [
  {
    key: "opdPatients",
    label: "OPD Patients",
    helper: "Visits today",
    path: "/medical-records",
    icon: UserGroupIcon,
    color: "text-blue-700",
    iconBg: "bg-blue-50",
    border: "border-blue-200",
  },
  {
    key: "admittedPatients",
    label: "IPD Admitted",
    helper: "Current census",
    path: "/bed-view",
    icon: BuildingOffice2Icon,
    color: "text-[#217E90]",
    iconBg: "bg-[#E9F5FE]",
    border: "border-[#b9dfe7]",
  },
  {
    key: "pendingDischarges",
    label: "Pending Discharge",
    helper: "No summary",
    path: "/discharge-summary",
    icon: ClipboardDocumentCheckIcon,
    color: "text-orange-700",
    iconBg: "bg-orange-50",
    border: "border-orange-200",
  },
  {
    key: "otCases",
    label: "OT Cases",
    helper: "Scheduled",
    path: "/ot-note",
    icon: CalendarDaysIcon,
    color: "text-purple-700",
    iconBg: "bg-purple-50",
    border: "border-purple-200",
  },
  {
    key: "pendingApprovals",
    label: "Pending Approvals",
    helper: "Pathology + PO",
    path: "/pathology-result-approval",
    icon: ExclamationTriangleIcon,
    color: "text-red-700",
    iconBg: "bg-red-50",
    border: "border-red-200",
  },
];

const WORKFLOW_ICONS = {
  round: UserCircleIcon,
  ot: CalendarDaysIcon,
  discharge: DocumentTextIcon,
  records: UserGroupIcon,
  pathology: BeakerIcon,
  purchase: ShoppingCartIcon,
  intake: ChartBarSquareIcon,
  tpr: HeartIcon,
  nursing: ClipboardDocumentCheckIcon,
  rmo: UserIcon,
};

const WORKFLOW_TONES = {
  teal: "bg-[#E9F5FE] text-[#217E90]",
  blue: "bg-blue-50 text-blue-700",
  orange: "bg-orange-50 text-orange-700",
  purple: "bg-purple-50 text-purple-700",
  red: "bg-red-50 text-red-700",
  amber: "bg-amber-50 text-amber-700",
  cyan: "bg-cyan-50 text-cyan-700",
  rose: "bg-rose-50 text-rose-700",
  green: "bg-emerald-50 text-emerald-700",
  slate: "bg-slate-100 text-slate-700",
};

const URGENCY_STYLES = {
  high: "bg-red-50 text-red-700 border-red-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200",
  normal: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const formatSnapshotDate = (date) => {
  if (!date) return "-";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(`${date}T00:00:00`));
};

const SectionHeader = ({ title, subtitle, actionLabel, onAction }) => (
  <div className="flex flex-wrap items-start justify-between gap-2 border-b border-gray-100 px-3 py-2.5 sm:px-4">
    <div>
      <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
      {subtitle && <p className="mt-0.5 text-[11px] text-gray-500">{subtitle}</p>}
    </div>
    {actionLabel && (
      <button
        type="button"
        onClick={onAction}
        className="inline-flex items-center gap-1 text-xs font-medium text-[#217E90] hover:text-[#1a5e70]"
      >
        {actionLabel}
        <ArrowRightIcon className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);

const KpiCard = ({ item, value, onClick }) => {
  const Icon = item.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group min-w-0 rounded-lg border ${item.border} bg-white p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#217E90]/30`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={`rounded-lg p-2 ${item.iconBg} ${item.color}`}>
          <Icon className="h-5 w-5" />
        </span>
        <ArrowRightIcon className="h-4 w-4 text-gray-300 transition group-hover:text-[#217E90]" />
      </div>
      <p className="mt-2 text-2xl font-bold leading-none text-gray-800">{value ?? 0}</p>
      <p className="mt-1.5 truncate text-xs font-semibold text-gray-700">{item.label}</p>
      <p className="mt-0.5 truncate text-[10px] text-gray-500">{item.helper}</p>
    </button>
  );
};

const WorkflowCard = ({ item, onClick }) => {
  const Icon = WORKFLOW_ICONS[item.id] || DocumentTextIcon;
  const tone = WORKFLOW_TONES[item.tone] || WORKFLOW_TONES.teal;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex min-w-0 items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left transition hover:border-[#75b8c5] hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-[#217E90]/30"
    >
      <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${tone}`}>
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center justify-between gap-2">
          <span className="truncate text-xs font-semibold text-gray-700">{item.title}</span>
          <span className="text-lg font-bold leading-none text-gray-800">{item.value}</span>
        </span>
        <span className="mt-1 block truncate text-[10px] text-gray-500">{item.detail}</span>
      </span>
      <ArrowRightIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-300 group-hover:text-[#217E90]" />
    </button>
  );
};

function DoctorDashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const dashboard = useSelector((state) => state.dashboard.data);
  const loading = useSelector((state) => state.dashboard.loading);
  const error = useSelector((state) => state.dashboard.error);
  const live = useSelector((state) => state.dashboard.live);

  useEffect(() => {
    dispatch(loadDoctorDashboard());
  }, [dispatch]);

  if (loading && !dashboard) {
    return (
      <div className="p-2 sm:p-3">
        <div className="min-h-[calc(100vh-5rem)] animate-pulse rounded-lg border bg-white p-4">
          <div className="h-6 w-56 rounded bg-gray-200" />
          <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="h-28 rounded-lg bg-gray-100" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="p-3 sm:p-4">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-center text-sm text-red-700">
          {error || "Unable to load dashboard."}
          <div className="mt-3">
            <button
              type="button"
              onClick={() => dispatch(loadDoctorDashboard())}
              className="rounded-md bg-[#217E90] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#1a5e70]"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-3.5rem)] p-1.5 pb-6 sm:p-2.5">
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-[#f8fbfd] shadow-sm">
        <header className="border-b border-gray-200 bg-white px-3 py-3 sm:px-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-base font-semibold text-[#217E90] sm:text-lg">
                  Doctor Dashboard
                </h1>
                {!live && (
                  <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                    Offline
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-xs text-gray-500">
                Welcome, {user?.userName || "Doctor"} · Snapshot overview for{" "}
                {formatSnapshotDate(dashboard.snapshotDate)}
              </p>
              {error && (
                <p className="mt-1 text-[11px] text-amber-700">{error}</p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => navigate("/medical-records")}
                className="rounded-md border border-[#217E90] bg-white px-3 py-1.5 text-xs font-medium text-[#217E90] hover:bg-[#E9F5FE]"
              >
                OPD Queue
              </button>
              <button
                type="button"
                onClick={() => navigate("/dr-round")}
                className="rounded-md bg-[#217E90] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#1a5e70]"
              >
                Start Round
              </button>
            </div>
          </div>
        </header>

        <main className="space-y-3 p-2.5 sm:p-3">
          <section aria-label="Key metrics">
            <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 lg:grid-cols-5">
              {KPI_CARDS.map((item) => (
                <KpiCard
                  key={item.key}
                  item={item}
                  value={dashboard.summary[item.key]}
                  onClick={() => navigate(item.path)}
                />
              ))}
            </div>
          </section>

          <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <SectionHeader
              title="Clinical Workflows"
              subtitle="Live module counts for today"
            />
            <div className="grid grid-cols-1 gap-2 p-2.5 sm:grid-cols-2 xl:grid-cols-5">
              {dashboard.workflowCards.map((item) => (
                <WorkflowCard
                  key={item.id}
                  item={item}
                  onClick={() => navigate(item.path)}
                />
              ))}
            </div>
          </section>

          <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <SectionHeader
              title="Priority Queue"
              subtitle="Patients requiring the doctor’s attention"
              actionLabel="View OPD queue"
              onAction={() => navigate("/medical-records")}
            />
            {dashboard.priorityQueue.length ? (
              <div className="hims-table-scroll">
                <table className="w-full min-w-[540px] text-left">
                  <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Patient</th>
                      <th className="px-3 py-2 font-semibold">Reference</th>
                      <th className="px-3 py-2 font-semibold">Task</th>
                      <th className="px-3 py-2 text-right font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-xs">
                    {dashboard.priorityQueue.map((item) => (
                      <tr key={item.id} className="hover:bg-[#f7fbfc]">
                        <td className="px-3 py-2.5 font-medium text-gray-800">{item.patientName}</td>
                        <td className="px-3 py-2.5 text-gray-500">{item.reference}</td>
                        <td className="px-3 py-2.5">
                          <span
                            className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                              URGENCY_STYLES[item.urgency] || URGENCY_STYLES.normal
                            }`}
                          >
                            {item.task}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <button
                            type="button"
                            onClick={() => navigate(item.path)}
                            className="font-medium text-[#217E90] hover:underline"
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="px-4 py-10 text-center text-xs text-gray-500">
                No priority patients in this snapshot.
              </div>
            )}
          </section>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
              <SectionHeader
                title="Bed Occupancy"
                subtitle={`${dashboard.occupancy.occupied} booked · ${dashboard.occupancy.available} available`}
                actionLabel="Open Bed View"
                onAction={() => navigate("/bed-view")}
              />
              <div className="p-3">
                <div className="mb-3 flex items-center gap-3 rounded-lg bg-[#f4fafb] p-3">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border-4 border-[#8fc5cf] bg-white text-sm font-bold text-[#217E90]">
                    {dashboard.occupancy.percent}%
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-700">Hospital occupancy</p>
                    <p className="text-[10px] text-gray-500">
                      {dashboard.occupancy.occupied} of {dashboard.occupancy.total} beds occupied
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {dashboard.wards.map((ward) => (
                    <div key={ward.wardId || ward.wardName} className="rounded-md border border-gray-100 p-2.5">
                      <div className="flex items-center justify-between gap-2 text-xs">
                        <span className="font-medium text-gray-700">{ward.wardName}</span>
                        <span className="text-gray-500">{ward.occupied}/{ward.total}</span>
                      </div>
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100">
                        <div
                          className="h-full rounded-full bg-[#217E90]"
                          style={{ width: `${ward.occupancyPercent}%` }}
                        />
                      </div>
                      <p className="mt-1.5 text-[10px] text-gray-500">{ward.available} beds available</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
              <SectionHeader
                title="Clinical Documentation"
                subtitle="Snapshot completion across admitted patients"
              />
              <div className="space-y-3 p-3">
                {dashboard.compliance.map((item) => {
                  const percent = item.total
                    ? Math.min(Math.round((item.completed / item.total) * 100), 100)
                    : 0;
                  return (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => navigate(item.path)}
                      className="block w-full rounded-md text-left focus:outline-none focus:ring-2 focus:ring-[#217E90]/30"
                    >
                      <span className="flex items-center justify-between gap-2 text-xs">
                        <span className="font-medium text-gray-700">{item.label}</span>
                        <span className="text-gray-500">
                          {item.completed}/{item.total} · {percent}%
                        </span>
                      </span>
                      <span className="mt-1.5 block h-2 overflow-hidden rounded-full bg-gray-100">
                        <span
                          className={`block h-full rounded-full ${
                            percent >= 75
                              ? "bg-emerald-500"
                              : percent >= 50
                                ? "bg-amber-500"
                                : "bg-red-500"
                          }`}
                          style={{ width: `${percent}%` }}
                        />
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
}

export default DoctorDashboard;
